import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import ContactBubbleModal from '../components/ContactBubbleModal';

interface NavigationProps {
  onNavigate: (id: string, serviceIdx?: number) => void;
}

const serviceDropdownItems = [
  { label: 'AI职场沟通辅助引擎', idx: 0 },
  { label: '【决战13周】销售思维训练桌游', idx: 1 },
  { label: 'REFRAME【重构】', idx: 2 },
  { label: '主题工作坊和积极心理研学', idx: 3 },
  { label: '持续护航服务', idx: 4 },
];

const brandDropdownItems = [
  { label: '荣誉资质', anchor: 'honor-wall' },
  { label: '公司理念', anchor: 'brand-concept' },
  { label: '发展历程', anchor: 'company-history' },
];

const aiDropdownItems = [
  { label: '组织变革心力教练', anchor: 'ai-chat#org-change' },
  { label: '组织行为分析', anchor: 'ai-chat#org-behavior' },
  { label: '梦境探索', anchor: 'relax' },
];

type DropdownKey = 'service' | 'brand' | 'ai' | null;

export default function Navigation({ onNavigate }: NavigationProps) {
  const [scrolled, setScrolled] = useState(false);
  const [activeId, setActiveId] = useState('hero');
  const [openDropdown, setOpenDropdown] = useState<DropdownKey>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClick = (id: string) => {
    setActiveId(id);
    setOpenDropdown(null);
    onNavigate(id);
  };

  const handleServiceClick = (idx: number) => {
    setActiveId('showcase');
    setOpenDropdown(null);
    onNavigate('showcase', idx);
  };

  const handleBrandClick = (anchor: string) => {
    setActiveId('brand-history');
    setOpenDropdown(null);
    onNavigate('brand-history');
    setTimeout(() => {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 400);
  };

  const handleAiClick = (anchor: string) => {
    setOpenDropdown(null);
    if (anchor === 'relax') {
      window.location.hash = '#/relax';
      return;
    }
    const [sectionId, subId] = anchor.split('#');
    setActiveId(sectionId);
    onNavigate(sectionId);
    if (subId) {
      setTimeout(() => {
        const el = document.getElementById(subId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
    }
  };

  const toggleDropdown = (key: DropdownKey) => {
    setOpenDropdown(openDropdown === key ? null : key);
  };

  const DropdownChevron = ({ isOpen }: { isOpen: boolean }) => (
    <span
      style={{
        marginLeft: '4px',
        fontSize: '0.55rem',
        opacity: 0.4,
        display: 'inline-block',
        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
        transition: 'transform 0.3s ease',
        color: isOpen ? '#b89850' : 'inherit',
      }}
    >
      ▼
    </span>
  );

  const DropdownMenu = ({ children, isOpen, menuKey }: { children: React.ReactNode; isOpen: boolean; menuKey: DropdownKey }) => (
    <div
      data-dropdown
      onMouseEnter={() => menuKey && setOpenDropdown(menuKey)}
      onMouseLeave={() => setOpenDropdown(null)}
      className="absolute top-full left-0 mt-3 transition-all duration-300 ease-out"
      style={{
        opacity: isOpen ? 1 : 0,
        visibility: isOpen ? 'visible' : 'hidden',
        transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
        backgroundColor: '#f0ebe1',
        borderRadius: '4px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.12), 0 0 0 1px rgba(160, 130, 80, 0.2)',
        minWidth: '240px',
        zIndex: 100,
        padding: '8px 0',
      }}
    >
      {/* Top triangle */}
      <div
        className="absolute"
        style={{
          top: '-6px',
          left: '32px',
          width: '12px',
          height: '12px',
          backgroundColor: '#f0ebe1',
          transform: 'rotate(45deg)',
          borderLeft: '1px solid rgba(160, 130, 80, 0.2)',
          borderTop: '1px solid rgba(160, 130, 80, 0.2)',
        }}
      />
      <div className="relative" style={{ zIndex: 1 }}>
        {children}
      </div>
    </div>
  );

  const DropdownItem = ({ onClick, label, prefix, isLast = false }: { onClick: () => void; label: string; prefix?: string; isLast?: boolean }) => (
    <button
      onClick={onClick}
      className="interactive w-full text-left transition-all duration-200"
      style={{
        fontSize: '0.8rem',
        color: '#4a3f35',
        letterSpacing: '0.02em',
        border: 'none',
        backgroundColor: 'transparent',
        whiteSpace: 'nowrap',
        padding: '11px 20px',
        borderBottom: isLast ? 'none' : '1px solid rgba(160, 130, 80, 0.12)',
        display: 'block',
        width: '100%',
        cursor: 'pointer',
        lineHeight: 1.5,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#e4d9c8';
        e.currentTarget.style.color = '#2c2418';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
        e.currentTarget.style.color = '#4a3f35';
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.backgroundColor = '#d8ccba';
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.backgroundColor = '#e4d9c8';
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {prefix && (
          <span
            style={{
              color: '#b89850',
              fontWeight: 600,
              marginRight: '10px',
              fontSize: '0.72rem',
              fontFamily: "'Noto Sans SC', sans-serif",
              minWidth: '22px',
            }}
          >
            {prefix}
          </span>
        )}
        <span style={{ fontWeight: 400 }}>{label}</span>
      </span>
    </button>
  );

  const NavPill = ({
    id,
    label,
    hasDropdown,
    dropdownKey,
    children,
  }: {
    id: string;
    label: string;
    hasDropdown?: boolean;
    dropdownKey?: DropdownKey;
    children?: React.ReactNode;
  }) => {
    const isActive = activeId === id;
    const isOpen = openDropdown === dropdownKey;

    return (
      <div data-dropdown className="relative">
        <button
          onClick={() => {
            if (hasDropdown && dropdownKey) {
              toggleDropdown(dropdownKey);
            } else {
              handleClick(id);
            }
          }}
          onMouseEnter={() => {
            if (hasDropdown && dropdownKey) {
              setOpenDropdown(dropdownKey);
            }
          }}
          className="interactive px-4 py-2 transition-all duration-300"
          style={{
            backgroundColor: isActive && !isOpen ? 'rgba(232, 122, 44, 0.12)' : 'transparent',
            color: isOpen ? '#b89850' : '#151414',
            boxShadow: isActive && !isOpen ? '0 0 0 1.5px #e87a2c, 0 0 8px rgba(232,122,44,0.2)' : 'none',
            borderRadius: '9999px',
            fontSize: '0.8rem',
            fontWeight: 400,
            letterSpacing: '0.02em',
            border: isActive && !isOpen ? 'none' : '1px solid transparent',
          }}
        >
          {label}
          {hasDropdown && <DropdownChevron isOpen={isOpen ?? false} />}
        </button>
        {children}
      </div>
    );
  };

  return (
    <nav
      ref={navRef}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        backgroundColor: scrolled ? 'rgba(252, 252, 252, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
      }}
    >
      <div className="flex items-center justify-between px-6 lg:px-10 py-4">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <span className="font-display tracking-wide" style={{ color: '#151414', fontSize: '1rem' }}>
            孔隙心理
          </span>
          <span style={{ color: 'rgba(21,20,20,0.2)', fontSize: '0.85rem' }}>×</span>
          <span className="font-display tracking-wide" style={{ color: '#6e5e50', fontSize: '0.85rem' }}>
            有心AI
          </span>
          <span
            style={{
              fontSize: '0.72rem',
              color: '#a08e78',
              marginLeft: '6px',
              paddingLeft: '6px',
              borderLeft: '1px solid #e8e0d4',
              fontWeight: 500,
              textShadow: '1px 1px 0 rgba(240,230,212,0.6), 2px 2px 0 rgba(180,160,130,0.2)',
            }}
          >
            10年+
          </span>
        </div>

        {/* Nav tabs */}
        <div className="hidden md:flex items-center gap-1.5">
          {isHome && (
            <>
              <NavPill id="hero" label="首页" />

              <NavPill id="brand-history" label="品牌" hasDropdown dropdownKey="brand">
                <DropdownMenu isOpen={openDropdown === 'brand'} menuKey="brand">
                  {brandDropdownItems.map((item, i) => (
                    <DropdownItem
                      key={item.anchor}
                      onClick={() => handleBrandClick(item.anchor)}
                      label={item.label}
                      isLast={i === brandDropdownItems.length - 1}
                    />
                  ))}
                </DropdownMenu>
              </NavPill>

              <NavPill id="showcase" label="服务" hasDropdown dropdownKey="service">
                <DropdownMenu isOpen={openDropdown === 'service'} menuKey="service">
                  {serviceDropdownItems.map((si, i) => (
                    <DropdownItem
                      key={si.idx}
                      onClick={() => handleServiceClick(si.idx)}
                      label={si.label}
                      prefix={String(si.idx + 1).padStart(2, '0')}
                      isLast={i === serviceDropdownItems.length - 1}
                    />
                  ))}
                </DropdownMenu>
              </NavPill>

              <NavPill id="custom" label="定制" />

              <NavPill id="ai-chat" label="AI体验" hasDropdown dropdownKey="ai">
                <DropdownMenu isOpen={openDropdown === 'ai'} menuKey="ai">
                  {aiDropdownItems.map((item, i) => (
                    <DropdownItem
                      key={item.label}
                      onClick={() => handleAiClick(item.anchor)}
                      label={item.label}
                      isLast={i === aiDropdownItems.length - 1}
                    />
                  ))}
                </DropdownMenu>
              </NavPill>
            </>
          )}

          <Link
            to="/assessment"
            className="interactive px-4 py-2 transition-all duration-300"
            style={{
              backgroundColor: isHome ? 'transparent' : 'rgba(232, 122, 44, 0.12)',
              color: '#151414',
              boxShadow: isHome ? 'none' : '0 0 0 1.5px #e87a2c, 0 0 8px rgba(232,122,44,0.2)',
              borderRadius: '9999px',
              fontSize: '0.8rem',
              fontWeight: 400,
              letterSpacing: '0.02em',
              border: '1px solid transparent',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(232, 122, 44, 0.12)';
              e.currentTarget.style.boxShadow = '0 0 0 1.5px #e87a2c, 0 0 8px rgba(232,122,44,0.2)';
            }}
            onMouseLeave={(e) => {
              if (isHome) {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.boxShadow = 'none';
              }
            }}
          >
            团队测评
          </Link>
        </div>

        <button
          onClick={() => setShowContactModal(true)}
          className="interactive px-5 py-2.5 transition-all duration-300 hover:scale-105"
          style={{
            backgroundColor: '#151414',
            color: '#fcfcfc',
            borderRadius: '9999px',
            fontSize: '0.8rem',
            letterSpacing: '0.02em',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          联系客服
        </button>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ backgroundColor: 'rgba(21, 20, 20, 0.08)' }} />

      {/* Contact Bubble Modal */}
      <ContactBubbleModal open={showContactModal} onClose={() => setShowContactModal(false)} />
    </nav>
  );
}
